import { useEffect, useMemo } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";

import FormFieldSpace from "components/FormFields/Space";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ComboBox from "ds/components/ComboBox";
import { Space } from "types/generated";
import Card from "ds/components/Card";

import AssignRoleComboBoxItem from "./ComboBoxItem";
import { AssignRoleFormEditFields } from "./types";
import AssignRoleFormTooltipInfo from "./TooltipInfo";

type AssignRoleFormEditProps = {
  roleAssignmentId: string;
  roleId: string;
  spaceId: string;
  selectableRoles: Array<{ id: string; name: string }>;
  selectableSpaces?: Space[];
  onEditCancel?: () => void;
  onEditSuccess?: () => void;
  setIsDirty: (id: string, isDirty: boolean) => void;
  onEdit: (formData: AssignRoleFormEditFields, roleAssignmentId: string) => void;
};

const AssignRoleFormEdit = ({
  roleAssignmentId,
  roleId,
  spaceId,
  onEditCancel,
  onEditSuccess,
  selectableRoles,
  selectableSpaces,
  setIsDirty,
  onEdit,
}: AssignRoleFormEditProps) => {
  const assignRoleForm = useForm<AssignRoleFormEditFields>({
    defaultValues: {
      roleId,
      space: spaceId,
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = assignRoleForm;

  const onSubmit: SubmitHandler<AssignRoleFormEditFields> = (formData) => {
    onEdit(formData, roleAssignmentId);
    reset();
    onEditSuccess?.();
    setIsDirty(roleAssignmentId, false);
  };

  const handleCancel = () => {
    reset();
    onEditCancel?.();
    setIsDirty(roleAssignmentId, false);
  };

  const options = useMemo(
    () => selectableRoles.map(({ id, name }) => ({ value: id, label: name })),
    [selectableRoles]
  );

  // TODO consider add a not existing space option to the list of spaces in case editing an output reference with not existing output
  useEffect(() => {
    setIsDirty(roleAssignmentId, isDirty);
  }, [isDirty, setIsDirty, roleAssignmentId]);

  return (
    <FormProvider {...assignRoleForm}>
      <Card variant="secondary" contentGap="x-large">
        <Controller
          name="roleId"
          control={control}
          rules={{ required: "Role field is required." }}
          render={({ field, fieldState }) => (
            <ComboBox
              tooltipInfo={<AssignRoleFormTooltipInfo />}
              items={options}
              label="Role"
              error={fieldState.error?.message}
              value={field.value}
              onChange={field.onChange}
            >
              {(item) => <AssignRoleComboBoxItem id={item.value} {...item} />}
            </ComboBox>
          )}
        />
        <FormFieldSpace label="Space" noMargin spaces={selectableSpaces} />

        <Box direction="row" justify="end" gap="medium">
          <Button variant="secondary" size="small" onPress={handleCancel}>
            Cancel
          </Button>

          <Button variant="contrast" size="small" onPress={() => handleSubmit(onSubmit)()}>
            Save
          </Button>
        </Box>
      </Card>
    </FormProvider>
  );
};

export default AssignRoleFormEdit;
